import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Icon } from '@/components/icons';
import { C1ChartCard, C1Tile } from '@/components/c1';
import type { GroupAnalytics } from '@/types/platform';

/* ── Palette ───────────────────────────────────────────────────────────
 *
 * The same validated categorical hues the institution panels use. Every
 * series is assigned by meaning, never cycled:
 *   blue  = headcount and the school's own figures,
 *   teal  = money that came in (collected) / academic positive rates,
 *   amber = money still owed (outstanding),
 *   grey  = the group's reference level, never a category.
 */

const BLUE = '#0057D2';
const TEAL = '#0D9E9E';
const AMBER = '#E9730E';
const GREY = '#6A6A85';

/**
 * Seat emphasis for the group home (same payload, different lead).
 *
 * Blueprint: G01/G04 → finance lead; G03 → academic lead; G06 → structure lead.
 * G02 / G05 have no distinct catalogue roles yet — they fall through to admin.
 */
type GroupEmphasis = 'finance' | 'academic' | 'admin';

function resolveEmphasis(roleCode: string | undefined, canSeeFinance: boolean): GroupEmphasis {
  if (roleCode === 'GROUP_ACADEMIC_DIRECTOR') return 'academic';
  if (roleCode === 'GROUP_ADMINISTRATOR' || roleCode === 'GROUP_AUDITOR') return 'admin';
  if (canSeeFinance) return 'finance';
  return 'admin';
}

/**
 * THE GROUP EXECUTIVE HOME (Wave 2): everything they administer, on one screen.
 *
 * A group seat covers every member school at once, so this panel reads the
 * group dashboard — the same aggregation core serves the platform console's
 * group analytics — and lays it out as a group of schools: headline counts on
 * tiles, fee settlement when the seat may see money, academic KPIs already on
 * the payload (admissions conversion, occupancy, progression, per-school
 * attendance), headcounts, a products-by-school matrix, and every figure in
 * the detail table below.
 *
 * NO COMPOSITE "HEALTH" SCORE. Core does not compute one and inventing weights
 * here would put a number on the screen that nobody set. Attendance is never
 * averaged across schools — the contract has no group attendance figure.
 *
 * Anything EduCore has not reported shows an em dash, not a zero.
 */
export function GroupAdminPanel({
  data,
  locale,
  canSeeFinance,
  roleCode,
}: {
  data: GroupAnalytics;
  locale: string;
  canSeeFinance: boolean;
  roleCode?: string;
}) {
  const { t } = useTranslation();
  const emphasis = resolveEmphasis(roleCode, canSeeFinance);
  const totals = data.financial?.totals ?? null;
  const groupCurrency = data.currencyBreakdown[0]?.currency ?? '';
  const money = (value: number, currency: string) =>
    `${value.toLocaleString(locale)}${currency ? ` ${currency}` : ''}`;
  const compactMoney = (value: number) =>
    new Intl.NumberFormat(locale, { notation: 'compact', maximumSignificantDigits: 3 }).format(
      value,
    );
  const rate = (value: number | null | undefined) =>
    value == null ? '—' : `${value.toFixed(1)}%`;
  const dash = '—';
  const countOrDash = (value: number | null | undefined) =>
    value == null ? dash : value.toLocaleString(locale);

  const financialByKey = new Map(
    (data.financial?.institutions ?? []).map((entry) => [entry.institutionId, entry]),
  );
  const rows = data.institutions.map((school) => {
    const fin = financialByKey.get(school.institutionId);
    return {
      key: school.institutionId,
      name: school.name,
      short: school.name.length > 14 ? `${school.name.slice(0, 12)}…` : school.name,
      status: school.status ?? null,
      academicYear: school.academicYear ?? null,
      campuses: school.campuses,
      branches: school.branches,
      available: fin?.available ?? false,
      students: fin?.students,
      staff: fin?.staff,
      parents: fin?.parents,
      classGroups: fin?.classGroups,
      payments: fin?.payments,
      billed: fin?.expectedFees,
      collected: fin?.collectedFees,
      outstanding: fin?.outstandingFees,
      rate: fin?.collectionRate ?? null,
      currency: fin?.currency ?? school.currency ?? null,
      productSubscriptions: school.productSubscriptions ?? [],
      conversionRate: fin?.admissions?.conversionRate,
      applications: fin?.admissions?.applications,
      occupancyRate: fin?.capacity?.occupancyRate,
      attendanceRate: fin?.attendanceRate,
      progressionRate: fin?.progression?.internalProgressionRate,
    };
  });
  const availableRows = rows.filter((row) => row.available);
  const financeAvailable = availableRows.length > 0;

  const hasAcademic =
    Boolean(totals?.admissions || totals?.capacity || totals?.progression) ||
    availableRows.some(
      (row) =>
        row.conversionRate != null ||
        row.occupancyRate != null ||
        row.attendanceRate != null ||
        row.progressionRate != null,
    );

  const settlement = [...availableRows]
    .sort((a, b) => (b.billed ?? 0) - (a.billed ?? 0))
    .map((row) => ({
      school: row.short,
      billed: row.billed ?? 0,
      collected: row.collected ?? 0,
      outstanding: row.outstanding ?? 0,
      currency: row.currency ?? '',
    }));

  const rates = availableRows
    .filter((row) => row.rate != null)
    .sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0))
    .map((row) => ({ school: row.short, rate: row.rate ?? 0 }));

  const headcount = [...availableRows]
    .sort((a, b) => (b.students ?? 0) - (a.students ?? 0))
    .map((row) => ({
      school: row.short,
      students: row.students ?? 0,
      staff: row.staff ?? 0,
    }));

  const occupancy = availableRows
    .filter((row) => row.occupancyRate != null)
    .sort((a, b) => (b.occupancyRate ?? 0) - (a.occupancyRate ?? 0))
    .map((row) => ({ school: row.short, rate: row.occupancyRate ?? 0 }));

  const conversion = availableRows
    .filter((row) => row.conversionRate != null)
    .sort((a, b) => (b.conversionRate ?? 0) - (a.conversionRate ?? 0))
    .map((row) => ({ school: row.short, rate: row.conversionRate ?? 0 }));

  const attendance = availableRows
    .filter((row) => row.attendanceRate != null)
    .sort((a, b) => (b.attendanceRate ?? 0) - (a.attendanceRate ?? 0))
    .map((row) => ({ school: row.short, rate: row.attendanceRate ?? 0 }));

  const groupRate = totals?.collectionRate ?? null;

  const childrenPerParent =
    totals?.students != null && totals.parents != null && totals.parents > 0
      ? totals.students / totals.parents
      : null;

  const families = availableRows
    .filter((row) => row.students != null && row.parents != null && row.parents > 0)
    .sort((a, b) => (b.students ?? 0) - (a.students ?? 0))
    .map((row) => ({
      school: row.short,
      childrenPerParent: (row.students ?? 0) / (row.parents ?? 1),
      parents: row.parents ?? 0,
    }));

  const countFor = new Map(
    data.productSubscriptions.map((entry) => [entry.productCode, entry.institutionCount]),
  );
  const productCodes = [
    ...new Set(data.institutions.flatMap((school) => school.productSubscriptions ?? [])),
  ].sort((a, b) => (countFor.get(b) ?? 0) - (countFor.get(a) ?? 0) || a.localeCompare(b));
  const productName = (code: string) =>
    ({
      EDUCORE: 'EduCore',
      LEARNHUB: 'LearnHub',
      INSTITUTION_FLOW: 'InstitutionFlow',
      PARENT_PORTAL: 'Parent Portal',
    })[code] ?? code;

  const structureTiles = (
    <div className="insight-tiles" key="structure-tiles">
      <Tile
        label={t('insights.groupAdmin.schools')}
        value={data.institutionCount}
        sub={t('insights.groupAdmin.activeOf', {
          active: data.activeInstitutionCount,
          total: data.institutionCount,
        })}
        icon="campus"
      />
      <Tile label={t('insights.groupAdmin.students')} value={totals ? totals.students : dash} icon="users" />
      <Tile label={t('insights.groupAdmin.parents')} value={totals?.parents ?? dash} icon="users" />
      <Tile
        label={t('insights.groupAdmin.childrenPerParent')}
        value={childrenPerParent != null ? childrenPerParent.toFixed(1) : dash}
        icon="role"
      />
      <Tile label={t('insights.groupAdmin.staff')} value={totals ? totals.staff : dash} icon="users" />
      <Tile
        label={t('insights.groupAdmin.classGroups')}
        value={totals?.classGroups ?? dash}
        icon="academic"
      />
    </div>
  );

  const financeTiles = canSeeFinance ? (
    <div className="insight-tiles" key="finance-tiles">
      <Tile label={t('insights.groupAdmin.payments')} value={totals?.payments ?? dash} icon="checklist" />
      <Tile
        label={t('insights.groupAdmin.billed')}
        value={totals ? `${compactMoney(totals.expectedFees ?? 0)} ${groupCurrency}`.trim() : dash}
        icon="report"
      />
      <Tile
        label={t('insights.groupAdmin.collected')}
        value={totals ? `${compactMoney(totals.collectedFees ?? 0)} ${groupCurrency}`.trim() : dash}
        icon="pulse"
      />
      <Tile
        label={t('insights.groupAdmin.outstanding')}
        value={totals ? `${compactMoney(totals.outstandingFees ?? 0)} ${groupCurrency}`.trim() : dash}
        icon="ban"
      />
      <Tile
        label={t('insights.groupAdmin.collectionRate')}
        value={totals ? rate(totals.collectionRate) : dash}
        icon="tasks"
      />
    </div>
  ) : null;

  const academicTiles = hasAcademic ? (
    <div className="insight-tiles" key="academic-tiles">
      <Tile
        label={t('insights.groupAdmin.conversionRate')}
        value={rate(totals?.admissions?.conversionRate)}
        sub={
          totals?.admissions?.applications != null
            ? t('insights.groupAdmin.applicationsSub', {
                count: totals.admissions.applications,
              })
            : undefined
        }
        icon="users"
      />
      <Tile
        label={t('insights.groupAdmin.occupancyRate')}
        value={rate(totals?.capacity?.occupancyRate)}
        sub={
          totals?.capacity?.totalEnrolled != null && totals?.capacity?.totalCapacity != null
            ? t('insights.groupAdmin.occupancySub', {
                enrolled: totals.capacity.totalEnrolled,
                capacity: totals.capacity.totalCapacity,
              })
            : undefined
        }
        icon="campus"
      />
      <Tile
        label={t('insights.groupAdmin.progressionRate')}
        value={rate(totals?.progression?.internalProgressionRate)}
        sub={
          totals?.progression?.eligibleCohort != null
            ? t('insights.groupAdmin.progressionSub', {
                count: totals.progression.eligibleCohort,
              })
            : undefined
        }
        icon="academic"
      />
      <Tile
        label={t('insights.groupAdmin.enrolledFromFunnel')}
        value={countOrDash(totals?.admissions?.enrolled)}
        icon="check"
      />
    </div>
  ) : null;

  const financeCharts = canSeeFinance ? (
    <>
      <C1ChartCard title={t('insights.groupAdmin.settlementTitle')}>
        {financeAvailable ? (
          <>
            <Legend
              items={[
                { color: TEAL, label: t('insights.groupAdmin.collected') },
                { color: AMBER, label: t('insights.groupAdmin.outstanding') },
              ]}
            />
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={settlement} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d5dadd" vertical={false} />
                <XAxis dataKey="school" interval={0} tick={{ fontSize: 11, fill: '#6a6a85' }} />
                <YAxis
                  width={52}
                  tickFormatter={(v: number) => compactMoney(v)}
                  tick={{ fontSize: 11, fill: '#6a6a85' }}
                />
                <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} content={<MoneyTooltip money={money} />} />
                <Bar dataKey="collected" stackId="fees" fill={TEAL} />
                <Bar dataKey="outstanding" stackId="fees" fill={AMBER} radius={[3, 3, 0, 0]}>
                  <LabelList
                    dataKey="billed"
                    position="top"
                    formatter={(v) => compactMoney(Number(v))}
                    fill="#444"
                    fontSize={11}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="c1-chartEmpty">{t('insights.groupAdmin.financialUnavailable')}</p>
        )}
      </C1ChartCard>

      <C1ChartCard title={t('insights.groupAdmin.rateTitle')}>
        {rates.length > 0 ? (
          <>
            <Legend
              items={[
                { color: BLUE, label: t('insights.groupAdmin.ratePerSchool') },
                { color: GREY, label: t('insights.groupAdmin.groupAverage') },
              ]}
            />
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={rates} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d5dadd" vertical={false} />
                <XAxis dataKey="school" interval={0} tick={{ fontSize: 11, fill: '#6a6a85' }} />
                <YAxis
                  domain={[0, 100]}
                  width={34}
                  tickFormatter={(v: number) => `${v}%`}
                  tick={{ fontSize: 11, fill: '#6a6a85' }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                  formatter={(v) => [`${Number(v ?? 0).toFixed(1)}%`, t('insights.groupAdmin.rate')]}
                />
                {groupRate != null && (
                  <ReferenceLine
                    y={groupRate}
                    stroke={GREY}
                    strokeDasharray="4 3"
                    label={{
                      value: `${t('insights.groupAdmin.groupAverage')} ${groupRate.toFixed(1)}%`,
                      position: 'insideTopRight',
                      fill: GREY,
                      fontSize: 11,
                    }}
                  />
                )}
                <Bar dataKey="rate" fill={BLUE} radius={[3, 3, 0, 0]}>
                  <LabelList
                    dataKey="rate"
                    position="top"
                    formatter={(v) => `${Number(v).toFixed(1)}%`}
                    fill="#444"
                    fontSize={11}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="c1-chartEmpty">{t('insights.groupAdmin.financialUnavailable')}</p>
        )}
      </C1ChartCard>
    </>
  ) : null;

  const academicCharts = hasAcademic ? (
    <>
      <C1ChartCard title={t('insights.groupAdmin.conversionTitle')}>
        {conversion.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={conversion} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d5dadd" vertical={false} />
              <XAxis dataKey="school" interval={0} tick={{ fontSize: 11, fill: '#6a6a85' }} />
              <YAxis
                domain={[0, 100]}
                width={34}
                tickFormatter={(v: number) => `${v}%`}
                tick={{ fontSize: 11, fill: '#6a6a85' }}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                formatter={(v) => [
                  `${Number(v ?? 0).toFixed(1)}%`,
                  t('insights.groupAdmin.conversionRate'),
                ]}
              />
              <Bar dataKey="rate" fill={TEAL} radius={[3, 3, 0, 0]}>
                <LabelList
                  dataKey="rate"
                  position="top"
                  formatter={(v) => `${Number(v).toFixed(1)}%`}
                  fill="#444"
                  fontSize={11}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="c1-chartEmpty">{t('insights.groupAdmin.academicUnavailable')}</p>
        )}
      </C1ChartCard>

      <C1ChartCard title={t('insights.groupAdmin.occupancyTitle')}>
        {occupancy.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={occupancy} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d5dadd" vertical={false} />
              <XAxis dataKey="school" interval={0} tick={{ fontSize: 11, fill: '#6a6a85' }} />
              <YAxis
                domain={[0, 100]}
                width={34}
                tickFormatter={(v: number) => `${v}%`}
                tick={{ fontSize: 11, fill: '#6a6a85' }}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                formatter={(v) => [
                  `${Number(v ?? 0).toFixed(1)}%`,
                  t('insights.groupAdmin.occupancyRate'),
                ]}
              />
              <Bar dataKey="rate" fill={BLUE} radius={[3, 3, 0, 0]}>
                <LabelList
                  dataKey="rate"
                  position="top"
                  formatter={(v) => `${Number(v).toFixed(1)}%`}
                  fill="#444"
                  fontSize={11}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="c1-chartEmpty">{t('insights.groupAdmin.academicUnavailable')}</p>
        )}
      </C1ChartCard>

      <C1ChartCard title={t('insights.groupAdmin.attendanceTitle')}>
        {attendance.length > 0 ? (
          <>
            <p className="insight-scopeNote">{t('insights.groupAdmin.attendanceNote')}</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={attendance} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d5dadd" vertical={false} />
                <XAxis dataKey="school" interval={0} tick={{ fontSize: 11, fill: '#6a6a85' }} />
                <YAxis
                  domain={[0, 100]}
                  width={34}
                  tickFormatter={(v: number) => `${v}%`}
                  tick={{ fontSize: 11, fill: '#6a6a85' }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                  formatter={(v) => [
                    `${Number(v ?? 0).toFixed(1)}%`,
                    t('insights.groupAdmin.attendanceRate'),
                  ]}
                />
                <Bar dataKey="rate" fill={AMBER} radius={[3, 3, 0, 0]}>
                  <LabelList
                    dataKey="rate"
                    position="top"
                    formatter={(v) => `${Number(v).toFixed(1)}%`}
                    fill="#444"
                    fontSize={11}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="c1-chartEmpty">{t('insights.groupAdmin.academicUnavailable')}</p>
        )}
      </C1ChartCard>
    </>
  ) : null;

  const peopleCharts = (
    <>
      <C1ChartCard title={t('insights.groupAdmin.headcountTitle')}>
        {financeAvailable ? (
          <>
            <Legend
              items={[
                { color: BLUE, label: t('insights.groupAdmin.students') },
                { color: TEAL, label: t('insights.groupAdmin.staff') },
              ]}
            />
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={headcount} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d5dadd" vertical={false} />
                <XAxis dataKey="school" interval={0} tick={{ fontSize: 11, fill: '#6a6a85' }} />
                <YAxis width={40} tick={{ fontSize: 11, fill: '#6a6a85' }} />
                <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
                <Bar dataKey="students" fill={BLUE} radius={[3, 3, 0, 0]} />
                <Bar dataKey="staff" fill={TEAL} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="c1-chartEmpty">{t('insights.groupAdmin.financialUnavailable')}</p>
        )}
      </C1ChartCard>

      <C1ChartCard title={t('insights.groupAdmin.familiesTitle')}>
        {families.length > 0 ? (
          <>
            <Legend items={[{ color: TEAL, label: t('insights.groupAdmin.childrenPerParent') }]} />
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={families} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d5dadd" vertical={false} />
                <XAxis dataKey="school" interval={0} tick={{ fontSize: 11, fill: '#6a6a85' }} />
                <YAxis width={34} tick={{ fontSize: 11, fill: '#6a6a85' }} />
                <Tooltip
                  cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                  formatter={(v) => [Number(v).toFixed(1), t('insights.groupAdmin.childrenPerParent')]}
                />
                <Bar dataKey="childrenPerParent" fill={TEAL} radius={[3, 3, 0, 0]}>
                  <LabelList
                    dataKey="childrenPerParent"
                    position="top"
                    formatter={(v) => Number(v).toFixed(1)}
                    fill="#444"
                    fontSize={11}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="c1-chartEmpty">{t('insights.groupAdmin.financialUnavailable')}</p>
        )}
      </C1ChartCard>
    </>
  );

  const matrix = (
    <C1ChartCard title={t('insights.groupAdmin.matrixTitle')} wide>
      <div className="insight-tableWrap">
        <table className="gmatrix">
          <thead>
            <tr>
              <th scope="col">{t('insights.groupAdmin.product')}</th>
              {rows.map((row) => (
                <th key={row.key} scope="col" className="gmatrix-school">
                  {row.short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {productCodes.map((code) => (
              <tr key={code}>
                <th scope="row">{productName(code)}</th>
                {rows.map((row) => {
                  const subscribed = row.productSubscriptions.includes(code);
                  return (
                    <td key={row.key} className="gmatrix-cell">
                      <span
                        className={subscribed ? 'gmatrixOk' : 'gmatrixNo'}
                        title={
                          subscribed
                            ? t('insights.groupAdmin.subscribed')
                            : t('insights.groupAdmin.notSubscribed')
                        }
                      >
                        {subscribed ? <Icon name="checkmark" size={14} aria-hidden="true" /> : dash}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </C1ChartCard>
  );

  const detailTable = (
    <div className="insight-tableWrap">
      <table className="insight-table insight-table--group">
        <caption className="sr-only">{t('insights.groupAdmin.detailTitle')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('insights.groupAdmin.school')}</th>
            <th scope="col">{t('insights.groupAdmin.academicYear')}</th>
            <th scope="col">{t('insights.groupAdmin.students')}</th>
            <th scope="col">{t('insights.groupAdmin.parents')}</th>
            <th scope="col">{t('insights.groupAdmin.classGroups')}</th>
            <th scope="col">{t('insights.groupAdmin.staff')}</th>
            {hasAcademic && <th scope="col">{t('insights.groupAdmin.conversionRate')}</th>}
            {hasAcademic && <th scope="col">{t('insights.groupAdmin.occupancyRate')}</th>}
            {hasAcademic && <th scope="col">{t('insights.groupAdmin.attendanceRate')}</th>}
            {hasAcademic && <th scope="col">{t('insights.groupAdmin.progressionRate')}</th>}
            {canSeeFinance && <th scope="col">{t('insights.groupAdmin.payments')}</th>}
            {canSeeFinance && <th scope="col">{t('insights.groupAdmin.billed')}</th>}
            {canSeeFinance && <th scope="col">{t('insights.groupAdmin.collected')}</th>}
            {canSeeFinance && <th scope="col">{t('insights.groupAdmin.outstanding')}</th>}
            {canSeeFinance && <th scope="col">{t('insights.groupAdmin.rate')}</th>}
            <th scope="col">{t('insights.groupAdmin.share')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.name}</th>
              <td>{row.academicYear ?? dash}</td>
              <td>{row.available ? (row.students ?? dash) : dash}</td>
              <td>{row.available ? (row.parents ?? dash) : dash}</td>
              <td>{row.available ? (row.classGroups ?? dash) : dash}</td>
              <td>{row.available ? (row.staff ?? dash) : dash}</td>
              {hasAcademic && <td>{row.available ? rate(row.conversionRate) : dash}</td>}
              {hasAcademic && <td>{row.available ? rate(row.occupancyRate) : dash}</td>}
              {hasAcademic && <td>{row.available ? rate(row.attendanceRate) : dash}</td>}
              {hasAcademic && <td>{row.available ? rate(row.progressionRate) : dash}</td>}
              {canSeeFinance && <td>{row.available ? (row.payments ?? dash) : dash}</td>}
              {canSeeFinance && (
                <td>{row.available ? money(row.billed ?? 0, row.currency ?? groupCurrency) : dash}</td>
              )}
              {canSeeFinance && (
                <td>
                  {row.available ? money(row.collected ?? 0, row.currency ?? groupCurrency) : dash}
                </td>
              )}
              {canSeeFinance && (
                <td>
                  {row.available ? money(row.outstanding ?? 0, row.currency ?? groupCurrency) : dash}
                </td>
              )}
              {canSeeFinance && <td>{row.available ? rate(row.rate) : dash}</td>}
              <td>
                {row.available && totals && totals.students > 0 && row.students != null
                  ? `${Math.round((row.students / totals.students) * 100)}%`
                  : dash}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const tileBlocks =
    emphasis === 'academic'
      ? [academicTiles, structureTiles, financeTiles]
      : emphasis === 'admin'
        ? [structureTiles, academicTiles, financeTiles]
        : [structureTiles, financeTiles, academicTiles];

  const chartBlocks =
    emphasis === 'academic'
      ? [academicCharts, peopleCharts, financeCharts, matrix]
      : emphasis === 'admin'
        ? [peopleCharts, matrix, academicCharts, financeCharts]
        : [financeCharts, peopleCharts, academicCharts, matrix];

  return (
    <>
      <p className="insight-scopeNote">
        {emphasis === 'academic'
          ? t('insights.groupAdmin.emphasisAcademic')
          : emphasis === 'admin'
            ? t('insights.groupAdmin.emphasisAdmin')
            : t('insights.groupAdmin.emphasisFinance')}
      </p>
      {tileBlocks}
      <div className="insight-charts">{chartBlocks}</div>
      {detailTable}
    </>
  );
}

/** A headline-count tile that reports a figure rather than linking to one. */
function Tile({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: Parameters<typeof Icon>[0]['name'];
}) {
  return <C1Tile icon={<Icon name={icon} size={16} />} label={label} value={value} sub={sub} />;
}

/** A compact series legend under the chart title. */
function Legend({ items }: { items: Array<{ color: string; label: string }> }) {
  return (
    <ul className="gchart-legend">
      {items.map((item) => (
        <li key={item.label} className="gchart-legendItem">
          <span className="gchart-legendDot" style={{ background: item.color }} aria-hidden="true" />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/** Tooltip for money charts: a per-school total that sums its own parts. */
function MoneyTooltip({
  active,
  payload,
  label,
  money,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string; payload?: { currency?: string } }>;
  label?: string;
  money: (value: number, currency: string) => string;
}) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  const parts = payload.filter((entry) => typeof entry.value === 'number' && entry.value > 0);
  const currency = payload[0]?.payload?.currency ?? '';
  const total = parts.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
  return (
    <div className="gchart-tooltip">
      <div className="gchart-tooltipTitle">{label}</div>
      {parts.map((entry) => (
        <div key={entry.name} className="gchart-tooltipRow">
          <span className="gchart-tooltipDot" style={{ background: entry.color }} aria-hidden="true" />
          <span>{entry.name}</span>
          <strong>{money(entry.value ?? 0, currency)}</strong>
        </div>
      ))}
      <div className="gchart-tooltipTotal">
        <span>{t('insights.groupAdmin.total')}</span>
        <strong>{money(total, currency)}</strong>
      </div>
    </div>
  );
}
